import React, { useState } from 'react';
import { Box, TextField, Button, MenuItem } from '@mui/material';
import { ImageUpload } from '../../atoms/UploadImage';
import axios from 'axios';
import { AddDocument } from '../../../services/technician';
import useSnackbar from '../../../hooks/useSnackbar';
import { useAuth } from '../../../store/AuthContext';

interface DocumentUploadProps {
  onClose: () => void;
  onSubmit: (data: any) => void;
  onReload: () => void;
  technicianId: string;
}

const documentOptions = [
  { value: 'PAN', label: 'PAN Card' },
  { value: 'PASSPORT', label: 'Passport' },
  { value: 'VOTER_ID', label: 'Voter ID' },
  { value: 'DRIVING_LICENSE', label: 'Driving License' },
];

export const DocumentUpload: React.FC<DocumentUploadProps> = ({
  onClose,
  onSubmit,
  onReload,
  technicianId,
}) => {
  const { authState } = useAuth();
  const [documentType, setDocumentType] = useState('');
  const [file, setFile] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const handleFileChange = (url: string) => {
    setFile(url);
  };

  const handleSubmit = async () => {
    if (!documentType || !file || !technicianId) {
      showSnackbar(
        'Please select a document type and upload a document.',
        'warning',
      );
      return;
    }
    setLoading(true);
    const data = {
      technicianId: technicianId, // replace with actual technicianId
      comment: comment || `Uploaded  by ${authState.userName}`,
      type: documentType,
      docUrl: file,
    };

    try {
      const responseStatus = await AddDocument(data);
      if (responseStatus === 200) {
        showSnackbar('Document added successfully.', 'success');
        setTimeout(() => {
          onReload(); // Reload data from props after submitting
          onClose();
          setLoading(false); // Close drawer after submitting
        }, 800);
      }
    } catch (error) {
      setLoading(false);
      console.error('Error adding document:', error);
      showSnackbar('Failed to add document. Please try again.', 'error');
    }
  };

  return (
    <Box component="form" sx={{ flex: 1 }}>
      {SnackbarComponent}
      <TextField
        select
        label="Document Type"
        variant="outlined"
        fullWidth
        sx={{ mb: 2 }}
        value={documentType}
        onChange={(e) => setDocumentType(e.target.value)}
      >
        {documentOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        label="Comment"
        variant="outlined"
        fullWidth
        multiline
        rows={4}
        sx={{ mb: 2 }}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
      />

      <ImageUpload
        imageUrl={file}
        onChange={handleFileChange}
        setLoader={setLoading}
        onRemove={() => setFile(null)}
        uploadLabel="Upload Document Image"
        fileId="document-upload-image"
        type="banner"
        folderType={4}
        disabled={false}
      />

      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          disabled={loading}
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </Box>
    </Box>
  );
};
